import {z} from 'zod';
import {
	editRemovesFromFilter,
	getDefaultFilters,
	makeOptionalPropsNullable,
	type EditRemovesFromFilterParams,
	type GetDefaultFiltersParams,
} from '@netx/core-next/schema';
import {
	ShareLinkSchema,
	type IShareLink,
	ShareLinkStatus,
	ShareLinkField,
} from './shareLinkSchema';

export const ShareLinkFilterSchema = makeOptionalPropsNullable(
	ShareLinkSchema.partial().pick({
		[ShareLinkField.creationDate]: true,
		[ShareLinkField.userLabel]: true,
		[ShareLinkField.expirationDate]: true,
		[ShareLinkField.status]: true,
		[ShareLinkField.title]: true,
		[ShareLinkField.token]: true,
	}),
).extend({
	// faux field to allow filtering on status by enabled/disabled
	enabled: z.boolean().nullable(),
});

export const ShareLinksFilterableField = ShareLinkFilterSchema.keyof().enum;

export const SHARE_LINK_FILTERABLE_FIELDS = Object.values(
	ShareLinksFilterableField,
);

export type ShareLinksFilter = z.infer<typeof ShareLinkFilterSchema>;

/** will return the filters passed in if valid/specified, otherwise will return the defaults for the invalid/unspecified */
export const getShareLinksDefaultFilters = (
	params?: Omit<GetDefaultFiltersParams<ShareLinksFilter>, 'filterableFields'>,
): ShareLinksFilter =>
	getDefaultFilters({
		...params,
		filterableFields: SHARE_LINK_FILTERABLE_FIELDS,
	});

/** given the original data, the new data, and the current filters, determine if the record should still be included in the filtered list */
export const shareLinkEditRemovesFromFilter = (
	params: Omit<
		EditRemovesFromFilterParams<IShareLink, ShareLinksFilter>,
		'schema'
	>,
) => {
	const updateNeeded = editRemovesFromFilter({
		...params,
		schema: ShareLinkSchema,
	});
	if (updateNeeded) return true;
	if (params.filters.enabled == null) return updateNeeded;
	// this is a faux filter field and does not actually directly match any props on the data object
	return params.filters.enabled === true
		? params.updatedData.status !== ShareLinkStatus.ACTIVE
		: params.updatedData.status === ShareLinkStatus.ACTIVE;
};
